import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff, Copy, MoreVertical, Star, Search, LayoutGrid, List, TrendingUp, TrendingDown, AlertCircle, CheckCircle2, X, DollarSign, ShoppingBag, Percent, ArrowRight, Zap, ExternalLink, Archive, ChevronRight, Calculator } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { Pagination } from '../ui/Pagination';
interface ServicesTabProps {
  showToast: (msg: string) => void;
  setActiveTab?: (tab: string) => void;
}
// --- MOCK DATA ---
const initialServices = [{
  id: 1,
  name: 'Разбор натальной карты',
  category: 'Натальная карта',
  price: 5000,
  duration: '60 мин',
  format: 'Видео-звонок',
  status: 'active',
  sales: 124,
  revenue: 620000,
  conversion: 18.5,
  rating: 4.9,
  returns: 2,
  isProblem: false
}, {
  id: 2,
  name: 'Соляр на год',
  category: 'Прогнозы',
  price: 4000,
  duration: '3 дня',
  format: 'Текстовый (PDF)',
  status: 'active',
  sales: 89,
  revenue: 356000,
  conversion: 12.3,
  rating: 4.7,
  returns: 1,
  isProblem: false
}, {
  id: 3,
  name: 'Синастрия (Совместимость)',
  category: 'Отношения',
  price: 6000,
  duration: '90 мин',
  format: 'Видео-звонок',
  status: 'active',
  sales: 45,
  revenue: 270000,
  conversion: 9.8,
  rating: 4.8,
  returns: 0,
  isProblem: false
}, {
  id: 4,
  name: 'Детский гороскоп',
  category: 'Натальная карта',
  price: 4500,
  duration: '5 дней',
  format: 'Текстовый (PDF)',
  status: 'draft',
  sales: 0,
  revenue: 0,
  conversion: 0,
  rating: 0,
  returns: 0,
  isProblem: true,
  problemText: 'Не заполнено описание'
}, {
  id: 5,
  name: 'Экспресс-ответ на вопрос',
  category: 'Консультации',
  price: 1500,
  duration: '24 часа',
  format: 'Чат',
  status: 'hidden',
  sales: 210,
  revenue: 315000,
  conversion: 22.1,
  rating: 4.6,
  returns: 5,
  isProblem: true,
  problemText: 'Высокий процент возвратов'
}];
export function ServicesTab({
  showToast,
  setActiveTab
}: ServicesTabProps) {
  // --- STATE ---
  const [services, setServices] = useState(initialServices);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // Modals state
  const [creatingService, setCreatingService] = useState(false);
  const [editingService, setEditingService] = useState<any | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  // --- COMPUTED DATA ---
  const filteredServices = useMemo(() => {
    return services.filter(s => {
      const matchesSearch = s.name.toLowerCase().includes(searchQuery.toLowerCase()) || s.category.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === 'all' || s.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [services, searchQuery, filterStatus]);
  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus]);
  const totalPages = Math.ceil(filteredServices.length / ITEMS_PER_PAGE);
  const paginatedServices = filteredServices.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalRevenue = services.reduce((sum, s) => sum + s.revenue, 0);
  const totalSales = services.reduce((sum, s) => sum + s.sales, 0);
  const avgCheck = totalSales > 0 ? Math.round(totalRevenue / totalSales) : 0;
  const bestService = [...services].sort((a, b) => b.revenue - a.revenue)[0];
  const worstService = [...services].filter(s => s.status === 'active').sort((a, b) => a.revenue - b.revenue)[0];
  const problemServices = services.filter(s => s.isProblem);
  // --- HANDLERS ---
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredServices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredServices.map(s => s.id)));
    }
  };
  const toggleSelect = (id: number) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setSelectedIds(newSet);
  };
  const handleStatusToggle = (id: number, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setServices(services.map(s => {
      if (s.id === id) {
        const newStatus = s.status === 'active' ? 'hidden' : 'active';
        showToast(`Услуга "${s.name}" ${newStatus === 'active' ? 'опубликована' : 'скрыта'}`);
        return {
          ...s,
          status: newStatus
        };
      }
      return s;
    }));
    setActiveDropdown(null);
  };
  const handleDelete = (id: number) => {
    setServices(services.filter(s => s.id !== id));
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
    setDeleteConfirmId(null);
    showToast('Услуга удалена');
  };
  const handleDuplicate = (service: any, e: React.MouseEvent) => {
    e.stopPropagation();
    const newService = {
      ...service,
      id: Date.now(),
      name: `${service.name} (Копия)`,
      status: 'draft',
      sales: 0,
      revenue: 0,
      returns: 0,
      isProblem: false
    };
    setServices([...services, newService]);
    showToast('Услуга дублирована');
    setActiveDropdown(null);
  };
  const handleBulkAction = (action: 'hide' | 'activate' | 'delete') => {
    if (action === 'delete') {
      setServices(services.filter(s => !selectedIds.has(s.id)));
      showToast(`Удалено услуг: ${selectedIds.size}`);
    } else {
      const newStatus = action === 'activate' ? 'active' : 'hidden';
      setServices(services.map(s => selectedIds.has(s.id) ? {
        ...s,
        status: newStatus
      } : s));
      showToast(`Статус изменен для ${selectedIds.size} услуг`);
    }
    setSelectedIds(new Set());
  };
  const navigateToOrders = (serviceName: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    showToast(`Переход к заказам: ${serviceName}`);
    if (setActiveTab) setActiveTab('orders');
  };
  const navigateToPromotion = (serviceName: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    showToast(`Переход к продвижению: ${serviceName}`);
    if (setActiveTab) setActiveTab('promotion');
  };
  // --- RENDERERS ---
  // 1. CREATE WIZARD MODAL
  const renderCreateWizard = () => {
    if (!creatingService) return null;
    return <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-15608">
        <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl animate-in fade-in zoom-in-95 duration-200" data-id="element-15609">
          <div className="sticky top-0 bg-white border-b border-slate-100 p-4 sm:p-6 flex justify-between items-center z-10" data-id="element-15610">
            <h3 className="text-xl font-bold text-slate-900" data-id="element-15611">
              Создание новой услуги
            </h3>
            <button onClick={() => setCreatingService(false)} className="text-slate-400 hover:text-slate-600" data-id="element-15612">
              <X className="w-6 h-6" data-id="element-15613" />
            </button>
          </div>
          <div className="p-4 sm:p-6 space-y-6" data-id="element-15614">
            <div className="space-y-4" data-id="element-15615">
              <div data-id="element-15616">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-15617">
                  Название услуги
                </label>
                <Input placeholder="Например: Разбор натальной карты" fullWidth data-id="element-15618" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-15619">
                <div data-id="element-15620">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-15621">
                    Категория
                  </label>
                  <select className="w-full rounded-xl border-slate-200 bg-slate-50 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500" data-id="element-15622">
                    <option data-id="element-15623">Натальная карта</option>
                    <option data-id="element-15624">Прогнозы</option>
                    <option data-id="element-15625">Отношения</option>
                    <option data-id="element-15626">Консультации</option>
                  </select>
                </div>
                <div data-id="element-15627">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-15628">
                    Формат
                  </label>
                  <select className="w-full rounded-xl border-slate-200 bg-slate-50 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500" data-id="element-15629">
                    <option data-id="element-15630">Видео-звонок</option>
                    <option data-id="element-15631">Текстовый (PDF)</option>
                    <option data-id="element-15632">Аудио-сообщение</option>
                    <option data-id="element-15633">Чат</option>
                  </select>
                </div>
              </div>

              {/* Calculator Block */}
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mt-6" data-id="element-15634">
                <h4 className="text-sm font-bold text-indigo-900 flex items-center gap-2 mb-3" data-id="element-15635">
                  <Calculator className="w-4 h-4" data-id="element-15636" /> Калькулятор дохода
                </h4>
                <div className="space-y-3" data-id="element-15637">
                  <div data-id="element-15638">
                    <label className="block text-xs font-medium text-indigo-700 mb-1" data-id="element-15639">
                      Цена для клиента (₽)
                    </label>
                    <Input type="number" defaultValue="5000" fullWidth className="bg-white" data-id="element-15640" />
                  </div>
                  <div className="flex justify-between items-center text-sm py-2 border-b border-indigo-200/50" data-id="element-15641">
                    <span className="text-indigo-600" data-id="element-15642">
                      Комиссия платформы (20%)
                    </span>
                    <span className="font-medium text-rose-600" data-id="element-15643">- 1 000 ₽</span>
                  </div>
                  <div className="flex justify-between items-center text-base font-bold pt-1" data-id="element-15644">
                    <span className="text-indigo-900" data-id="element-15645">Ваш чистый доход</span>
                    <span className="text-emerald-600" data-id="element-15646">4 000 ₽</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 sm:p-6 flex justify-end gap-3 z-10" data-id="element-15647">
            <Button variant="outline" onClick={() => setCreatingService(false)} data-id="element-15648">
              Отмена
            </Button>
            <Button variant="primary" onClick={() => {
            showToast('Услуга успешно создана и сохранена в черновики');
            setCreatingService(false);
          }} data-id="element-15649">
              Сохранить черновик
            </Button>
            <Button variant="primary" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => {
            showToast('Услуга успешно опубликована');
            setCreatingService(false);
          }} data-id="element-15650">
              Опубликовать
            </Button>
          </div>
        </div>
      </div>;
  };
  // 2. EDIT MODAL
  const renderEditModal = () => {
    if (!editingService) return null;
    return <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-15651">
        <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl animate-in fade-in zoom-in-95 duration-200" data-id="element-15652">
          <div className="sticky top-0 bg-white border-b border-slate-100 p-4 sm:p-6 flex justify-between items-center z-10" data-id="element-15653">
            <h3 className="text-xl font-bold text-slate-900" data-id="element-15654">
              Редактирование: {editingService.name}
            </h3>
            <button onClick={() => setEditingService(null)} className="text-slate-400 hover:text-slate-600" data-id="element-15655">
              <X className="w-6 h-6" data-id="element-15656" />
            </button>
          </div>
          <div className="p-4 sm:p-6 space-y-4" data-id="element-15657">
            <div data-id="element-15658">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-15659">
                Название услуги
              </label>
              <Input defaultValue={editingService.name} fullWidth data-id="element-15660" />
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-15661">
              <div data-id="element-15662">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-15663">
                  Цена (₽)
                </label>
                <Input type="number" defaultValue={editingService.price} fullWidth data-id="element-15664" />
              </div>
              <div data-id="element-15665">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-15666">
                  Длительность
                </label>
                <Input defaultValue={editingService.duration} fullWidth data-id="element-15667" />
              </div>
            </div>
            {editingService.isProblem && <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3 mt-4" data-id="element-15668">
                <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" data-id="element-15669" />
                <div data-id="element-15670">
                  <h4 className="text-sm font-bold text-rose-900" data-id="element-15671">
                    Проблема: {editingService.problemText}
                  </h4>
                  <p className="text-xs text-rose-700 mt-1" data-id="element-15672">
                    Исправьте проблему, чтобы увеличить конверсию.
                  </p>
                </div>
              </div>}
          </div>
          <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 sm:p-6 flex justify-between items-center z-10" data-id="element-15673">
            <Button variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => {
            setDeleteConfirmId(editingService.id);
            setEditingService(null);
          }} data-id="element-15674">
              Удалить
            </Button>
            <div className="flex gap-3" data-id="element-15675">
              <Button variant="outline" onClick={() => setEditingService(null)} data-id="element-15676">
                Отмена
              </Button>
              <Button variant="primary" onClick={() => {
              showToast('Изменения сохранены');
              setEditingService(null);
            }} data-id="element-15677">
                Сохранить
              </Button>
            </div>
          </div>
        </div>
      </div>;
  };
  // 3. DELETE CONFIRM MODAL
  const renderDeleteConfirm = () => {
    if (!deleteConfirmId) return null;
    const service = services.find(s => s.id === deleteConfirmId);
    return <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-id="element-15678">
        <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl animate-in zoom-in-95 duration-200 text-center" data-id="element-15679">
          <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-15680">
            <Trash2 className="w-8 h-8 text-rose-600" data-id="element-15681" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-15682">
            Удалить услугу?
          </h3>
          <p className="text-slate-500 mb-6" data-id="element-15683">
            Вы уверены, что хотите удалить "{service?.name}"? Это действие
            нельзя отменить.
          </p>
          <div className="flex gap-3" data-id="element-15684">
            <Button variant="outline" fullWidth onClick={() => setDeleteConfirmId(null)} data-id="element-15685">
              Отмена
            </Button>
            <Button variant="primary" fullWidth className="bg-rose-600 hover:bg-rose-700" onClick={() => handleDelete(deleteConfirmId)} data-id="element-15686">
              Удалить
            </Button>
          </div>
        </div>
      </div>;
  };
  // --- MAIN RENDER ---
  return <div className="space-y-6 animate-in fade-in duration-300 pb-20" data-id="element-15687">
      {renderCreateWizard()}
      {renderEditModal()}
      {renderDeleteConfirm()}

      {/* 1. HEADER */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-15688">
        <div data-id="element-15689">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-15690">Услуги</h2>
          <p className="text-slate-500 text-sm" data-id="element-15691">
            Управление вашими консультациями и продуктами
          </p>
        </div>
        <Button variant="primary" icon={<Plus className="w-4 h-4" data-id="element-15693" />} onClick={() => setCreatingService(true)} data-id="element-15692">
          Создать услугу
        </Button>
      </div>

      {/* 2. KPI BLOCK */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4" data-id="element-15694">
        <Card padding="sm" className="border-slate-200 hover:border-indigo-300 cursor-pointer transition-colors" onClick={() => setFilterStatus('all')} data-id="element-15695">
          <div className="text-xs text-slate-500 font-medium mb-1 uppercase tracking-wider" data-id="element-15696">
            Общая выручка
          </div>
          <div className="text-xl font-bold text-slate-900 mb-1" data-id="element-15697">
            {totalRevenue.toLocaleString('ru-RU')} ₽
          </div>
          <div className="text-[10px] font-bold text-emerald-600 flex items-center" data-id="element-15698">
            <TrendingUp className="w-3 h-3 mr-0.5" data-id="element-15699" /> +12% за месяц
          </div>
        </Card>
        <Card padding="sm" className="border-slate-200 hover:border-indigo-300 cursor-pointer transition-colors" onClick={() => setFilterStatus('all')} data-id="element-15700">
          <div className="text-xs text-slate-500 font-medium mb-1 uppercase tracking-wider" data-id="element-15701">
            Продажи
          </div>
          <div className="text-xl font-bold text-slate-900 mb-1" data-id="element-15702">
            {totalSales}
          </div>
          <div className="text-[10px] font-bold text-emerald-600 flex items-center" data-id="element-15703">
            <TrendingUp className="w-3 h-3 mr-0.5" data-id="element-15704" /> +5% за месяц
          </div>
        </Card>
        <Card padding="sm" className="border-slate-200 hover:border-indigo-300 cursor-pointer transition-colors" data-id="element-15705">
          <div className="text-xs text-slate-500 font-medium mb-1 uppercase tracking-wider" data-id="element-15706">
            Средний чек
          </div>
          <div className="text-xl font-bold text-slate-900 mb-1" data-id="element-15707">
            {avgCheck.toLocaleString('ru-RU')} ₽
          </div>
          <div className="text-[10px] font-bold text-slate-500 flex items-center" data-id="element-15708">
            По всем услугам
          </div>
        </Card>
        <Card padding="sm" className="border-slate-200 hover:border-emerald-300 cursor-pointer transition-colors bg-emerald-50/30" onClick={() => {
        setSearchQuery(bestService?.name || '');
      }} data-id="element-15709">
          <div className="text-xs text-slate-500 font-medium mb-1 uppercase tracking-wider" data-id="element-15710">
            Лучшая услуга
          </div>
          <div className="text-sm font-bold text-slate-900 truncate" title={bestService?.name} data-id="element-15711">
            {bestService?.name || '-'}
          </div>
          <div className="text-[10px] font-bold text-emerald-600 mt-1" data-id="element-15712">
            {bestService?.sales} продаж
          </div>
        </Card>
        <Card padding="sm" className="border-slate-200 hover:border-rose-300 cursor-pointer transition-colors bg-rose-50/30" onClick={() => {
        setSearchQuery(worstService?.name || '');
      }} data-id="element-15713">
          <div className="text-xs text-slate-500 font-medium mb-1 uppercase tracking-wider" data-id="element-15714">
            Худшая услуга
          </div>
          <div className="text-sm font-bold text-slate-900 truncate" title={worstService?.name} data-id="element-15715">
            {worstService?.name || '-'}
          </div>
          <div className="text-[10px] font-bold text-rose-600 mt-1" data-id="element-15716">
            Нужно улучшить
          </div>
        </Card>
      </div>

      {/* 3. PROBLEM SERVICES ALERT */}
      {problemServices.length > 0 && <Card padding="md" className="border-rose-200 bg-rose-50/50" data-id="element-15717">
          <div className="flex items-center gap-2 mb-3" data-id="element-15718">
            <AlertCircle className="w-5 h-5 text-rose-600" data-id="element-15719" />
            <h3 className="text-base font-bold text-rose-900" data-id="element-15720">
              Проблемные услуги ({problemServices.length})
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-15721">
            {problemServices.map(s => <div key={`prob-${s.id}`} className="bg-white p-3 rounded-xl border border-rose-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 shadow-sm cursor-pointer hover:border-rose-300 transition-colors" onClick={() => setEditingService(s)} data-id="element-15722">
                <div data-id="element-15723">
                  <div className="text-sm font-bold text-slate-900" data-id="element-15724">
                    {s.name}
                  </div>
                  <div className="text-xs text-rose-600 mt-0.5" data-id="element-15725">
                    {s.problemText}
                  </div>
                </div>
                <div className="flex gap-2 w-full sm:w-auto" data-id="element-15726">
                  <Button variant="outline" size="sm" className="flex-1 sm:flex-none text-rose-600 border-rose-200 hover:bg-rose-50" onClick={e => {
              e.stopPropagation();
              setEditingService(s);
            }} data-id="element-15727">
                    Исправить
                  </Button>
                  <Button variant="primary" size="sm" className="flex-1 sm:flex-none bg-indigo-600 hover:bg-indigo-700" onClick={e => navigateToPromotion(s.name, e)} data-id="element-15728">
                    Реклама
                  </Button>
                </div>
              </div>)}
          </div>
        </Card>}

      {/* 4. REVENUE GROWTH RECOMMENDATIONS */}
      <Card padding="md" className="border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-purple-50/50" data-id="element-15729">
        <div className="flex items-center gap-2 mb-3" data-id="element-15730">
          <Zap className="w-5 h-5 text-indigo-600" data-id="element-15731" />
          <h3 className="text-base font-bold text-indigo-900" data-id="element-15732">
            Точки роста дохода
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-15733">
          <div className="bg-white p-3 rounded-xl border border-indigo-100 shadow-sm flex flex-col justify-between" data-id="element-15734">
            <div data-id="element-15735">
              <div className="text-sm font-bold text-slate-900 mb-1" data-id="element-15736">
                Поднимите цену на "Разбор натальной карты"
              </div>
              <p className="text-xs text-slate-600 mb-3" data-id="element-15737">
                Конверсия 18.5% — это очень высоко. Вы можете безопасно поднять
                цену на 10-15%.
              </p>
            </div>
            <Button variant="outline" size="sm" className="w-full text-indigo-700 border-indigo-200" onClick={() => {
            const s = services.find(s => s.id === 1);
            if (s) setEditingService(s);
          }} data-id="element-15738">
              Применить
            </Button>
          </div>
          <div className="bg-white p-3 rounded-xl border border-indigo-100 shadow-sm flex flex-col justify-between" data-id="element-15739">
            <div data-id="element-15740">
              <div className="text-sm font-bold text-slate-900 mb-1" data-id="element-15741">
                Создайте пакетную услугу
              </div>
              <p className="text-xs text-slate-600 mb-3" data-id="element-15742">
                Клиенты часто покупают Соляр после Натальной карты. Объедините
                их со скидкой 10%.
              </p>
            </div>
            <Button variant="primary" size="sm" className="w-full bg-indigo-600" onClick={() => setCreatingService(true)} data-id="element-15743">
              Создать пакет
            </Button>
          </div>
        </div>
      </Card>

      {/* 5. SEARCH, FILTERS & VIEW TOGGLE */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-15744">
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto" data-id="element-15745">
          <div className="relative w-full sm:w-64" data-id="element-15746">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-15747" />
            <input type="text" placeholder="Поиск услуг..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-15748" />
          </div>
          <Button variant={showFilters ? 'primary' : 'outline'} icon={<Search className="w-4 h-4" data-id="element-15750" />} onClick={() => setShowFilters(!showFilters)} className="w-full sm:w-auto" data-id="element-15749">
            Фильтры
          </Button>
        </div>

        <div className="flex bg-slate-100 p-1 rounded-lg w-full sm:w-auto" data-id="element-15751">
          <button onClick={() => setViewMode('table')} className={`flex-1 sm:flex-none px-3 py-1.5 text-sm font-medium rounded-md flex items-center justify-center gap-2 transition-colors ${viewMode === 'table' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`} data-id="element-15752">
            <List className="w-4 h-4" data-id="element-15753" />{' '}
            <span className="hidden sm:inline" data-id="element-15754">Таблица</span>
          </button>
          <button onClick={() => setViewMode('cards')} className={`flex-1 sm:flex-none px-3 py-1.5 text-sm font-medium rounded-md flex items-center justify-center gap-2 transition-colors ${viewMode === 'cards' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`} data-id="element-15755">
            <LayoutGrid className="w-4 h-4" data-id="element-15756" />{' '}
            <span className="hidden sm:inline" data-id="element-15757">Карточки</span>
          </button>
        </div>
      </div>

      {/* FILTER PANEL */}
      {showFilters && <Card padding="sm" className="border-slate-200 bg-slate-50 animate-in slide-in-from-top-2 duration-200" data-id="element-15758">
          <div className="flex flex-wrap gap-2" data-id="element-15759">
            <button onClick={() => setFilterStatus('all')} className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterStatus === 'all' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-15760">
              Все статусы
            </button>
            <button onClick={() => setFilterStatus('active')} className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterStatus === 'active' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-700 border-emerald-200 hover:border-emerald-300'}`} data-id="element-15761">
              Активные
            </button>
            <button onClick={() => setFilterStatus('hidden')} className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterStatus === 'hidden' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-200 hover:border-amber-300'}`} data-id="element-15762">
              Скрытые
            </button>
            <button onClick={() => setFilterStatus('draft')} className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterStatus === 'draft' ? 'bg-slate-500 text-white border-slate-500' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-15763">
              Черновики
            </button>
          </div>
        </Card>}

      {/* BULK ACTIONS BAR */}
      {selectedIds.size > 0 && <div className="bg-indigo-600 text-white p-3 rounded-xl flex flex-col sm:flex-row justify-between items-center gap-4 animate-in slide-in-from-bottom-4 shadow-lg sticky top-4 z-40" data-id="element-15764">
          <div className="text-sm font-medium" data-id="element-15765">
            Выбрано услуг: {selectedIds.size}
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto" data-id="element-15766">
            <Button variant="outline" size="sm" className="bg-white/10 border-white/20 text-white hover:bg-white/20 flex-1 sm:flex-none" onClick={() => handleBulkAction('activate')} data-id="element-15767">
              <Eye className="w-4 h-4 mr-1.5" data-id="element-15768" /> Активировать
            </Button>
            <Button variant="outline" size="sm" className="bg-white/10 border-white/20 text-white hover:bg-white/20 flex-1 sm:flex-none" onClick={() => handleBulkAction('hide')} data-id="element-15769">
              <EyeOff className="w-4 h-4 mr-1.5" data-id="element-15770" /> Скрыть
            </Button>
            <Button variant="outline" size="sm" className="bg-rose-500/20 border-rose-500/30 text-rose-100 hover:bg-rose-500/40 flex-1 sm:flex-none" onClick={() => handleBulkAction('delete')} data-id="element-15771">
              <Trash2 className="w-4 h-4 mr-1.5" data-id="element-15772" /> Удалить
            </Button>
          </div>
        </div>}

      {/* 6. MAIN CONTENT (TABLE OR CARDS) */}
      {filteredServices.length === 0 ? <Card padding="lg" className="text-center border-dashed border-2 border-slate-200" data-id="element-15773">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-15774">
            <Search className="w-8 h-8 text-slate-300" data-id="element-15775" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-15776">
            Услуги не найдены
          </h3>
          <p className="text-slate-500 mb-6" data-id="element-15777">
            Попробуйте изменить параметры поиска или фильтры.
          </p>
          <Button variant="primary" onClick={() => {
        setSearchQuery('');
        setFilterStatus('all');
      }} data-id="element-15778">
            Сбросить фильтры
          </Button>
        </Card> : viewMode === 'table' /* TABLE VIEW */ ? <Card padding="none" className="border-slate-200 overflow-visible" data-id="element-15779">
          <div className="overflow-x-auto" data-id="element-15780">
            <table className="w-full text-left whitespace-nowrap" data-id="element-15781">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wider" data-id="element-15782">
                <tr data-id="element-15783">
                  <th className="px-4 py-3 w-10 text-center" data-id="element-15784">
                    <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" checked={selectedIds.size === filteredServices.length && filteredServices.length > 0} onChange={toggleSelectAll} data-id="element-15785" />
                  </th>
                  <th className="px-4 py-3 font-medium" data-id="element-15786">Название</th>
                  <th className="px-4 py-3 font-medium" data-id="element-15787">Цена</th>
                  <th className="px-4 py-3 font-medium" data-id="element-15788">Статус</th>
                  <th className="px-4 py-3 font-medium" data-id="element-15789">Продажи / Выручка</th>
                  <th className="px-4 py-3 font-medium" data-id="element-15790">Конверсия</th>
                  <th className="px-4 py-3 font-medium" data-id="element-15791">Рейтинг</th>
                  <th className="px-4 py-3 text-right font-medium" data-id="element-15792">Действия</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-15793">
                {paginatedServices.map(s => <tr key={s.id} className={`hover:bg-slate-50 transition-colors ${selectedIds.has(s.id) ? 'bg-indigo-50/30' : ''}`} data-id="element-15794">
                    <td className="px-4 py-3 text-center" data-id="element-15795">
                      <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" checked={selectedIds.has(s.id)} onChange={() => toggleSelect(s.id)} data-id="element-15796" />
                    </td>
                    <td className="px-4 py-3" data-id="element-15797">
                      <div className="text-sm font-bold text-indigo-600 hover:text-indigo-800 cursor-pointer flex items-center gap-2" onClick={() => setEditingService(s)} data-id="element-15798">
                        {s.name}
                        {s.isProblem && <AlertCircle className="w-3.5 h-3.5 text-rose-500" title={s.problemText} data-id="element-15799" />}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-15800">
                        {s.category} • {s.format}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900" data-id="element-15801">
                      {s.price.toLocaleString('ru-RU')} ₽
                    </td>
                    <td className="px-4 py-3" data-id="element-15802">
                      <div className="cursor-pointer inline-block" onClick={e => handleStatusToggle(s.id, e)} title="Нажмите для переключения" data-id="element-15803">
                        {s.status === 'active' && <Badge variant="success" size="sm" data-id="element-15804">
                            Активна
                          </Badge>}
                        {s.status === 'draft' && <Badge variant="neutral" size="sm" data-id="element-15805">
                            Черновик
                          </Badge>}
                        {s.status === 'hidden' && <Badge variant="warning" size="sm" data-id="element-15806">
                            Скрыта
                          </Badge>}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-15807">
                      <div className="text-sm font-bold text-indigo-600 hover:text-indigo-800 cursor-pointer flex items-center gap-1" onClick={e => navigateToOrders(s.name, e)} data-id="element-15808">
                        {s.sales} шт. <ChevronRight className="w-3 h-3" data-id="element-15809" />
                      </div>
                      <div className="text-xs font-medium text-emerald-600" data-id="element-15810">
                        {s.revenue > 0 ? `${s.revenue.toLocaleString('ru-RU')} ₽` : '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-15811">
                      <div className="text-sm text-slate-700" data-id="element-15812">
                        {s.conversion > 0 ? `${s.conversion}%` : '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-15813">
                      {s.rating > 0 ? <div className="flex items-center gap-1 text-sm font-medium text-slate-700" data-id="element-15814">
                          <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" data-id="element-15815" />{' '}
                          {s.rating}
                        </div> : <span className="text-slate-400 text-sm" data-id="element-15816">-</span>}
                      {s.returns > 0 && <div className="text-[10px] text-rose-500 mt-0.5" data-id="element-15817">
                          {s.returns} возвратов
                        </div>}
                    </td>
                    <td className="px-4 py-3 text-right relative" data-id="element-15818">
                      <div className="flex items-center justify-end gap-1" data-id="element-15819">
                        <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-indigo-600" title="Редактировать" onClick={() => setEditingService(s)} data-id="element-15820">
                          <Edit2 className="w-4 h-4" data-id="element-15821" />
                        </Button>
                        <Button variant="ghost" size="sm" className={`w-8 h-8 p-0 ${s.status === 'active' ? 'text-slate-400 hover:text-amber-600' : 'text-slate-400 hover:text-emerald-600'}`} title={s.status === 'active' ? 'Скрыть' : 'Опубликовать'} onClick={e => handleStatusToggle(s.id, e)} data-id="element-15822">
                          {s.status === 'active' ? <EyeOff className="w-4 h-4" data-id="element-15823" /> : <Eye className="w-4 h-4" data-id="element-15824" />}
                        </Button>
                        <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-slate-900" onClick={e => {
                    e.stopPropagation();
                    setActiveDropdown(activeDropdown === s.id ? null : s.id);
                  }} data-id="element-15825">
                          <MoreVertical className="w-4 h-4" data-id="element-15826" />
                        </Button>
                      </div>

                      {/* Dropdown Menu */}
                      {activeDropdown === s.id && <div className="absolute right-8 top-10 w-48 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-50 animate-in fade-in zoom-in-95" onClick={e => e.stopPropagation()} data-id="element-15827">
                          <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => handleDuplicate(s, e)} data-id="element-15828">
                            <Copy className="w-4 h-4 text-slate-400" data-id="element-15829" />{' '}
                            Дублировать
                          </button>
                          <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => navigateToPromotion(s.name, e)} data-id="element-15830">
                            <Zap className="w-4 h-4 text-amber-500" data-id="element-15831" />{' '}
                            Продвигать
                          </button>
                          <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => {
                    e.stopPropagation();
                    showToast('Публичная страница открыта');
                    setActiveDropdown(null);
                  }} data-id="element-15832">
                            <ExternalLink className="w-4 h-4 text-slate-400" data-id="element-15833" />{' '}
                            Открыть страницу
                          </button>
                          <div className="h-px bg-slate-100 my-1" data-id="element-15834"></div>
                          <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => {
                    e.stopPropagation();
                    showToast('Услуга в архиве');
                    setActiveDropdown(null);
                  }} data-id="element-15835">
                            <Archive className="w-4 h-4 text-slate-400" data-id="element-15836" /> В
                            архив
                          </button>
                          <button className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2" onClick={e => {
                    e.stopPropagation();
                    setDeleteConfirmId(s.id);
                    setActiveDropdown(null);
                  }} data-id="element-15837">
                            <Trash2 className="w-4 h-4 text-rose-500" data-id="element-15838" /> Удалить
                          </button>
                        </div>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </Card> /* CARDS VIEW (Mobile friendly) */ : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-15839">
          {paginatedServices.map(s => <Card key={s.id} padding="md" className={`border-slate-200 relative ${selectedIds.has(s.id) ? 'ring-2 ring-indigo-500' : ''}`} data-id="element-15840">
              <div className="absolute top-4 right-4 flex gap-2" data-id="element-15841">
                <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-5 h-5" checked={selectedIds.has(s.id)} onChange={() => toggleSelect(s.id)} data-id="element-15842" />
              </div>

              <div className="mb-4 pr-8" data-id="element-15843">
                <div className="flex items-center gap-2 mb-2" data-id="element-15844">
                  {s.status === 'active' && <Badge variant="success" size="sm" data-id="element-15845">
                      Активна
                    </Badge>}
                  {s.status === 'draft' && <Badge variant="neutral" size="sm" data-id="element-15846">
                      Черновик
                    </Badge>}
                  {s.status === 'hidden' && <Badge variant="warning" size="sm" data-id="element-15847">
                      Скрыта
                    </Badge>}
                  {s.isProblem && <AlertCircle className="w-4 h-4 text-rose-500" title={s.problemText} data-id="element-15848" />}
                </div>
                <h3 className="text-lg font-bold text-slate-900 cursor-pointer hover:text-indigo-600" onClick={() => setEditingService(s)} data-id="element-15849">
                  {s.name}
                </h3>
                <p className="text-sm text-slate-500" data-id="element-15850">
                  {s.category} • {s.format}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4 bg-slate-50 p-3 rounded-xl" data-id="element-15851">
                <div data-id="element-15852">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1" data-id="element-15853">
                    Цена
                  </div>
                  <div className="font-bold text-slate-900" data-id="element-15854">
                    {s.price.toLocaleString('ru-RU')} ₽
                  </div>
                </div>
                <div className="cursor-pointer hover:bg-slate-200/50 rounded p-1 -m-1 transition-colors" onClick={e => navigateToOrders(s.name, e)} data-id="element-15855">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1" data-id="element-15856">
                    Продажи
                  </div>
                  <div className="font-bold text-indigo-600 flex items-center" data-id="element-15857">
                    {s.sales} <ChevronRight className="w-3 h-3 ml-0.5" data-id="element-15858" />
                  </div>
                </div>
                <div data-id="element-15859">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1" data-id="element-15860">
                    Выручка
                  </div>
                  <div className="font-bold text-emerald-600" data-id="element-15861">
                    {s.revenue > 0 ? `${s.revenue.toLocaleString('ru-RU')} ₽` : '-'}
                  </div>
                </div>
                <div data-id="element-15862">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1" data-id="element-15863">
                    Конверсия
                  </div>
                  <div className="font-bold text-slate-900" data-id="element-15864">
                    {s.conversion > 0 ? `${s.conversion}%` : '-'}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-15865">
                <div className="flex gap-1" data-id="element-15866">
                  <Button variant="outline" size="sm" className="w-9 h-9 p-0" onClick={() => setEditingService(s)} data-id="element-15867">
                    <Edit2 className="w-4 h-4 text-slate-600" data-id="element-15868" />
                  </Button>
                  <Button variant="outline" size="sm" className="w-9 h-9 p-0" onClick={e => handleStatusToggle(s.id, e)} data-id="element-15869">
                    {s.status === 'active' ? <EyeOff className="w-4 h-4 text-amber-600" data-id="element-15870" /> : <Eye className="w-4 h-4 text-emerald-600" data-id="element-15871" />}
                  </Button>
                </div>
                <Button variant="ghost" size="sm" className="text-slate-500" onClick={e => {
            e.stopPropagation();
            setActiveDropdown(activeDropdown === s.id ? null : s.id);
          }} data-id="element-15872">
                  Еще <MoreVertical className="w-4 h-4 ml-1" data-id="element-15873" />
                </Button>
              </div>

              {/* Mobile Dropdown Menu */}
              {activeDropdown === s.id && <div className="absolute right-4 bottom-14 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-50 animate-in fade-in zoom-in-95" onClick={e => e.stopPropagation()} data-id="element-15874">
                  <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => handleDuplicate(s, e)} data-id="element-15875">
                    <Copy className="w-4 h-4 text-slate-400" data-id="element-15876" /> Дублировать
                  </button>
                  <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => navigateToPromotion(s.name, e)} data-id="element-15877">
                    <Zap className="w-4 h-4 text-amber-500" data-id="element-15878" /> Продвигать
                  </button>
                  <div className="h-px bg-slate-100 my-1" data-id="element-15879"></div>
                  <button className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2" onClick={e => {
            e.stopPropagation();
            setDeleteConfirmId(s.id);
            setActiveDropdown(null);
          }} data-id="element-15880">
                    <Trash2 className="w-4 h-4 text-rose-500" data-id="element-15881" /> Удалить
                  </button>
                </div>}
            </Card>)}
        </div>}

      {/* Pagination */}
      {totalPages > 1 && <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2" data-id="element-15882">
          <span className="text-xs text-slate-500" data-id="element-15883">
            Показано {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
            {Math.min(currentPage * ITEMS_PER_PAGE, filteredServices.length)} из{' '}
            {filteredServices.length}
          </span>
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-15884" />
        </div>}
    </div>;
}